'use client';

import Link from 'next/link';
import { Category } from '@/types';
import { useLanguage } from '@/contexts/LanguageContext';

interface CategoryGridProps {
  categories: Category[];
  counts: Record<string, number>;
}

const iconMap: Record<string, string> = {
  'building-office': '🏢',
  'scale': '⚖️',
  'calculator': '🧮',
  'computer': '💻',
  'users': '👥',
  'briefcase': '💼',
  'megaphone': '📣',
  'building': '🏛️',
};

export default function CategoryGrid({ categories, counts }: CategoryGridProps) {
  const { t } = useLanguage();

  return (
    <section id="categories" className="py-20 bg-gray-50 dark:bg-[#1e293b] transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h3 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] dark:text-white mb-4">{t('categories.title')}</h3>
          <p className="text-lg text-gray-600 dark:text-gray-400">{t('categories.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((category) => (
            <Link
              key={category.id}
              href={`/category/${category.id}`}
              className="category-card bg-white dark:bg-[#0f172a] rounded-xl p-6 card-hover cursor-pointer border-2 border-transparent hover:border-[#d4a574] transition-colors duration-300"
            >
              <div className="category-icon text-[#1e3a5f] dark:text-[#d4a574] text-4xl mb-4">
                {iconMap[category.icon] || '📋'}
              </div>
              <h4 className="text-xl font-bold text-[#1e3a5f] dark:text-white mb-2">{category.name}</h4>
              <p className="text-gray-600 dark:text-gray-400 mb-3">{category.description}</p>
              <p className="text-sm font-semibold text-[#d4a574]">{counts[category.id] || 0} Providers</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
